import React, { useState, useEffect } from 'react';
import { Tabs, Card, Row, Col, Statistic, Spin, Alert } from 'antd';
import {
  RobotOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { Typography } from 'antd';
import { KrybotService } from '../../services/api/krybotService';
import { KRYBOT_AGENT_TYPES, type AgentType } from '../../types/krybot';
import { AgentManager } from './AgentManagement/AgentManager';
import { TemplateManager } from './TemplateManagement/TemplateManager';
import { OrchestrationPanel } from './OrchestrationPanel';
import styles from './KrybotDashboard.module.css';

const { Text } = Typography;

export const KrybotDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const emptyByType = (): Record<AgentType, number> =>
    Object.fromEntries(KRYBOT_AGENT_TYPES.map((t) => [t, 0])) as Record<AgentType, number>;

  const [stats, setStats] = useState({
    activeAgents: 0,
    activeAgentsByType: emptyByType(),
    pendingApprovals: 0,
    activeRuns: 0,
  });
  const [activeTab, setActiveTab] = useState('agents');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [agents, runs, approvals] = await Promise.all([
        KrybotService.listAgents({ is_active: true }).catch(() => []),
        KrybotService.listAgentRuns({ status_filter: 'in_progress' }).catch(() => []),
        KrybotService.listApprovals({ status_filter: 'pending' }).catch(() => ({ approvals: [], total: 0 })),
      ]);

      const activeAgentsByType = emptyByType();
      for (const a of agents) {
        const t = a.agent_type as AgentType | undefined;
        if (t && t in activeAgentsByType) {
          activeAgentsByType[t] += 1;
        }
      }

      setStats({
        activeAgents: agents.length,
        activeAgentsByType,
        pendingApprovals: approvals.total || 0,
        activeRuns: runs.length,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && stats.activeAgents === 0) {
    return (
      <div className={styles.krybotDashboardLoading} style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading Krybot Dashboard...</div>
      </div>
    );
  }

  return (
    <div className={styles.krybotDashboard}>
      {error && (
        <Alert
          message="Error Loading Dashboard"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Status Overview Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Agents"
              value={stats.activeAgents}
              prefix={<RobotOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Approvals"
              value={stats.pendingApprovals}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Runs"
              value={stats.activeRuns}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Agent types (legacy C/P/Co)"
              value={`${stats.activeAgentsByType.chat} / ${stats.activeAgentsByType.prime} / ${stats.activeAgentsByType.company}`}
              prefix={<RobotOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 24 }} title="Active agents by type (all)">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
          {KRYBOT_AGENT_TYPES.map((t) => (
            <Text key={t} type="secondary">
              {t}: <strong>{stats.activeAgentsByType[t]}</strong>
            </Text>
          ))}
        </div>
      </Card>

      {/* Main Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          items={[
            {
              key: 'agents',
              label: (
                <span>
                  <RobotOutlined />
                  Agents
                </span>
              ),
              children: <AgentManager onRefresh={loadStats} />,
            },
            {
              key: 'templates',
              label: (
                <span>
                  <FileTextOutlined />
                  Templates
                </span>
              ),
              children: <TemplateManager />,
            },
            {
              key: 'orchestration',
              label: (
                <span>
                  <ApartmentOutlined />
                  Orchestration
                </span>
              ),
              children: <OrchestrationPanel />,
            },
          ]}
        />
      </Card>
    </div>
  );
};

